import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChrome } from "../ui/MobileShell";

/* MobileIDCapture (design 7b) — full-height identity-verification flow: progress,
   one instruction, a framed viewfinder, three tips, then the shutter CTA in thumb
   reach, with "Upload from library" beneath.

   REAL submit paths (preserve the KYC gate exactly):
     • DeepIDV enabled (GET /kyc/status .deepidv_enabled) → "Take photo" starts
       the hosted redirect session (POST /kyc/deepidv/start → session_url). This
       is the live, automated path.
     • Otherwise → the manual path: capture/choose the ID front then a selfie and
       POST /kyc/submit (multipart id_document + selfie). The on-device camera is
       the OS file picker with capture="environment"/"user" (a real browser can't
       mock a live camera feed — we never fake one).

   Full-height chrome: bare (no app bars) — a back X + a 3-seg progress bar. */

const GREEN = "#00D632";

const TIPS = [
  { ic: "sparkles", t: "Even lighting, no glare" },
  { ic: "id-card", t: "All four corners inside the frame" },
  { ic: "check", t: "Text sharp and readable" },
];

export default function MobileIDCapture() {
  useChrome({ bare: true });
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [deepidv, setDeepidv] = useState(null); // null = loading, bool once known
  const [stage, setStage] = useState("front"); // front | selfie (manual path)
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const idInputRef = useRef(null);
  const selfieInputRef = useRef(null);

  useEffect(() => {
    api.get("/kyc/status").then(({ data }) => setDeepidv(!!data?.deepidv_enabled)).catch(() => setDeepidv(false));
  }, []);

  const startDeepidv = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/kyc/deepidv/start", {});
      if (data?.session_url) { window.location.href = data.session_url; return; }
      setErr("Could not start identity verification. Please try again.");
    } catch (e) {
      setErr(apiError(e?.response?.data?.detail) || "Could not start identity verification.");
    } finally { setBusy(false); }
  };

  const onPick = (file) => {
    setErr("");
    if (stage === "front") { setIdFile(file); setStage("selfie"); }
    else { setSelfieFile(file); }
  };

  const submitManual = async () => {
    if (!idFile || !selfieFile) { setErr("Attach both your ID and a selfie."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("id_document", idFile);
      fd.append("selfie", selfieFile);
      await api.post("/kyc/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setDone(true);
    } catch (e) {
      setErr(apiError(e?.response?.data?.detail) || "Could not submit your documents.");
    } finally { setBusy(false); }
  };

  // Which of the 3 segments are filled (step 2 of 3 per the design: ID front).
  const filled = stage === "front" ? 1 : 2;

  if (done) {
    return (
      <Frame onClose={() => navigate("/account")} filled={3}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 24px" }}>
          <span style={{ width: 72, height: 72, borderRadius: "50%", background: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="check" size={32} style={{ color: "#0A0A0A" }} />
          </span>
          <div style={{ marginTop: 16, font: "800 26px 'Manrope'", letterSpacing: "-.03em", color: "#fff" }}>Documents submitted</div>
          <p style={{ marginTop: 8, font: "500 14px/1.6 'Outfit'", color: "#a1a1aa" }}>We'll review them shortly and email you. Identity verification unlocks PCash redemptions.</p>
        </div>
        <div style={{ padding: "0 20px calc(20px + var(--m-safe-bot))" }}>
          <button type="button" onClick={() => navigate("/account")} style={greenCta}>Back to Account</button>
        </div>
      </Frame>
    );
  }

  const title = stage === "front" ? "Front of your ID" : "Take a selfie";
  const desc = stage === "front"
    ? "Driver's license, state ID or passport. Needed once to redeem PCash."
    : "A quick selfie so we can match it to your ID. Face the camera in good light.";
  const currentFile = stage === "front" ? idFile : selfieFile;

  return (
    <Frame onClose={() => navigate(-1)} filled={deepidv ? 1 : filled}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "8px 20px 0", display: "flex", flexDirection: "column" }}>
        <div style={{ font: "800 28px/1.05 'Manrope'", letterSpacing: "-.03em", color: "#fff" }}>{title}</div>
        <p style={{ marginTop: 8, font: "500 14px/1.5 'Outfit'", color: "#a1a1aa" }}>{desc}</p>

        {/* viewfinder */}
        <div style={{ position: "relative", height: 226, marginTop: 20, borderRadius: 20, overflow: "hidden", background: "radial-gradient(80% 80% at 50% 40%,#16161B 0%,#0A0A0A 75%)" }}>
          {["tl", "tr", "bl", "br"].map((c) => (
            <span key={c} style={{
              position: "absolute", width: 34, height: 34,
              top: c[0] === "t" ? 14 : undefined, bottom: c[0] === "b" ? 14 : undefined,
              left: c[1] === "l" ? 14 : undefined, right: c[1] === "r" ? 14 : undefined,
              borderTop: c[0] === "t" ? "3px solid #F4F4F5" : undefined,
              borderBottom: c[0] === "b" ? "3px solid #F4F4F5" : undefined,
              borderLeft: c[1] === "l" ? "3px solid #F4F4F5" : undefined,
              borderRight: c[1] === "r" ? "3px solid #F4F4F5" : undefined,
              borderTopLeftRadius: c === "tl" ? 10 : undefined, borderTopRightRadius: c === "tr" ? 10 : undefined,
              borderBottomLeftRadius: c === "bl" ? 10 : undefined, borderBottomRightRadius: c === "br" ? 10 : undefined,
            }} />
          ))}
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Icon name={currentFile ? "check-circle" : "id-card"} size={38} style={{ color: currentFile ? GREEN : "#52525b" }} />
            <span style={{ font: "700 10px 'Outfit'", letterSpacing: ".14em", color: currentFile ? GREEN : "#71717a" }}>
              {currentFile ? "READY" : "CAMERA VIEW"}
            </span>
          </div>
        </div>

        {/* tips */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
          {TIPS.map((x) => (
            <div key={x.t} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Icon name={x.ic} size={18} style={{ color: GREEN, flex: "none" }} />
              <span style={{ font: "500 14px 'Outfit'", color: "#d4d4d8" }}>{x.t}</span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1 }} />
        <p style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, font: "500 12px 'Outfit'", color: "#71717a", margin: "18px 0 0" }}>
          <Icon name="lock" size={13} style={{ color: "#71717a" }} /> Encrypted · used only to verify you
        </p>
        {err && <div style={{ marginTop: 10, textAlign: "center", font: "600 13px 'Outfit'", color: "#EF4444" }}>{err}</div>}
      </div>

      {/* hidden OS camera/file pickers (manual path) */}
      <input ref={idInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
      <input ref={selfieInputRef} type="file" accept="image/*" capture="user" style={{ display: "none" }}
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />

      {/* CTA zone */}
      <div style={{ padding: "12px 20px calc(16px + var(--m-safe-bot))", display: "flex", flexDirection: "column", gap: 10 }}>
        {deepidv === null ? (
          <button type="button" disabled style={{ ...greenCta, opacity: 0.5 }}>Loading…</button>
        ) : deepidv ? (
          // Automated hosted verification — the honest "shutter" is starting the real session.
          <button type="button" onClick={startDeepidv} disabled={busy} style={{ ...greenCta, opacity: busy ? 0.7 : 1 }}>
            <Icon name="shield" size={18} style={{ color: "#0A0A0A" }} /> {busy ? "Starting…" : "Verify my identity"}
          </button>
        ) : (
          // Manual path — capture this step, then submit once both are attached.
          <>
            {stage === "selfie" && idFile && selfieFile ? (
              <button type="button" onClick={submitManual} disabled={busy} style={{ ...greenCta, opacity: busy ? 0.7 : 1 }}>
                {busy ? "Submitting…" : "Submit for review"}
              </button>
            ) : (
              <button type="button" onClick={() => (stage === "front" ? idInputRef : selfieInputRef).current?.click()}
                style={greenCta}>
                <Icon name="id-card" size={18} style={{ color: "#0A0A0A" }} />
                {currentFile ? "Retake photo" : "Take photo"}
              </button>
            )}
            <button type="button" onClick={() => (stage === "front" ? idInputRef : selfieInputRef).current?.click()}
              style={{ height: 44, background: "none", border: 0, cursor: "pointer", font: "600 15px 'Outfit'", color: "#a1a1aa" }}>
              Upload from library
            </button>
          </>
        )}
      </div>
    </Frame>
  );
}

// Full-height frame: a top row with a close X, the screen title, step counter,
// and a 3-segment progress bar.
function Frame({ children, onClose, filled = 1 }) {
  return (
    <div className="m-app" style={{ position: "fixed", inset: 0, zIndex: 50, background: "#0A0A0A", display: "flex", flexDirection: "column", paddingTop: "var(--m-safe-top)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px 0" }}>
        <button type="button" aria-label="Close" onClick={onClose}
          style={{ width: 40, height: 40, flex: "none", borderRadius: 12, background: "rgba(255,255,255,.08)", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="x" size={18} style={{ color: "#fff" }} />
        </button>
        <span style={{ flex: 1, textAlign: "center", font: "800 18px 'Manrope'", color: "#fff" }}>Verify identity</span>
        <span style={{ width: 40, flex: "none", textAlign: "right", font: "600 12px 'Outfit'", color: "#71717a" }}>{filled} of 3</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, padding: "12px 16px 6px" }}>
        {[1, 2, 3].map((i) => (
          <span key={i} style={{ height: 4, borderRadius: 999, background: i <= filled ? "#F4F4F5" : "#26262B" }} />
        ))}
      </div>
      {children}
    </div>
  );
}

const greenCta = {
  width: "100%", height: 56, borderRadius: 16, background: GREEN, color: "#0A0A0A",
  font: "800 16px 'Manrope'", border: 0, cursor: "pointer",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
};
